import Box from '@mui/material/Box'
import {
  DataGridPremium,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  type GridCellParams,
  type GridColDef,
  type GridPaginationModel,
  type GridRowId,
} from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import { useCallback, useState } from 'react'
import type { FraudCase } from '../types'
import { FraudCaseDetailPanel } from './FraudCaseDetailPanel'

type FraudCasesTableProps = {
  cases: FraudCase[]
  isLoading: boolean
  hiddenColumns?: string[]
  initialExpandedCaseId?: string
  rowCount: number
  paginationModel: GridPaginationModel
  onPaginationModelChange: (model: GridPaginationModel) => void
}

const allColumns: GridColDef<FraudCase>[] = [
  { field: 'id', headerName: 'Case ID', minWidth: 120, flex: 1, sortable: false },
  { field: 'status', headerName: 'Status', minWidth: 100, sortable: false },
  { field: 'resolution', headerName: 'Resolution', minWidth: 110, sortable: false },
  { field: 'customerId', headerName: 'Customer', minWidth: 120, flex: 1, sortable: false },
  {
    field: 'transactions',
    headerName: 'Transactions',
    minWidth: 110,
    sortable: false,
    valueGetter: (_value, row) => row.transactions.length,
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    minWidth: 160,
    sortable: false,
    valueFormatter: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
  },
]

export const FraudCasesTable = ({
  cases,
  isLoading,
  hiddenColumns,
  initialExpandedCaseId,
  rowCount,
  paginationModel,
  onPaginationModelChange,
}: FraudCasesTableProps) => {
  const columns = hiddenColumns ? allColumns.filter((col) => !hiddenColumns.includes(col.field)) : allColumns
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(initialExpandedCaseId ? new Set([initialExpandedCaseId]) : new Set())
  const getDetailPanelContent = useCallback(({ row }: { row: FraudCase }) => <FraudCaseDetailPanel fraudCase={row} />, [])
  const getDetailPanelHeight = useCallback(() => 'auto' as const, [])

  const handleCellClick = useCallback(({ id, field }: GridCellParams) => {
    if (field === GRID_DETAIL_PANEL_TOGGLE_FIELD) return
    setExpandedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  return (
    <Box sx={{ maxWidth: '93vw' }}>
      <DataGridPremium
        rows={cases}
        columns={columns}
        loading={isLoading}
        autoHeight
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        detailPanelExpandedRowIds={expandedRowIds}
        onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
        onCellClick={handleCellClick}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row')}
        sx={{
          '& .odd-row': { backgroundColor: 'action.hover' },
          '& .MuiDataGrid-row': { borderBottom: '1px solid', borderColor: 'divider' },
        }}
      />
    </Box>
  )
}
